import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { WebcamImage, WebcamInitError, WebcamUtil } from 'ngx-webcam';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

@Component({
  selector: 'app-modal-webcam',
  templateUrl: './modal-webcam.component.html',
  styleUrls: ['./modal-webcam.component.scss'],
})
export class ModalWebcamComponent implements OnInit {
  // toggle webcam on/off
  public showWebcam = true;
  public allowCameraSwitch = true;
  public multipleWebcamsAvailable = false;
  public deviceId = '';
  public videoOptions: MediaTrackConstraints = {};
  public errors: WebcamInitError[] = [];

  // latest snapshot
  public webcamImage: any;

  // webcam snapshot trigger
  private trigger: Subject<void> = new Subject<void>();

  // switch to next / previous / specific webcam; true/false: forward/backwards, string: deviceId
  private nextWebcam: Subject<boolean | string> = new Subject<
    boolean | string
  >();

  @ViewChild('player') player: ElementRef<HTMLMediaElement>;
  stream: any = null;
  mediaRecorder: MediaRecorder = null;
  blobsRecorded: any = [];
  videoUrl: string = null;

  constructor(
    public dialogRef: MatDialogRef<ModalWebcamComponent>,
    private cd: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: { photo: boolean }
  ) {}

  public async ngOnInit(): Promise<void> {
    WebcamUtil.getAvailableVideoInputs().then(
      (mediaDevices: MediaDeviceInfo[]) => {
        this.multipleWebcamsAvailable = mediaDevices && mediaDevices.length > 1;
      }
    );

    if (!this.data.photo) {
      this.startVideo();
    }
  }

  get resultModal(): any {
    if (this.data.photo) {
      return this.webcamImage?.imageAsDataUrl;
    } else {
      return this.blobsRecorded;
    }
  }

  get dataModal(): any {
    if (this.data.photo) {
      return this.webcamImage?.imageAsDataUrl;
    } else {
      return this.videoUrl;
    }
  }

  resetModal(): void {
    if (this.data.photo) {
      this.webcamImage = null;
    } else {
      this.videoUrl = null;
      this.stream = null;
      this.startVideo();
    }
  }

  public triggerSnapshot(): void {
    if (this.errors.length > 0) {
      return;
    }

    if (this.data.photo) {
      this.trigger.next();
    } else if (this.mediaRecorder?.state === 'recording') {
      this.stopVideo();
    } else {
      this.recordVideo();
    }
  }

  private async startVideo(): Promise<void> {
    const constraints = {
      audio: false,
      video: { width: 1458, height: 750 },
    };
    this.stream = await navigator.mediaDevices.getUserMedia(constraints);

    this.player.nativeElement.srcObject = this.stream;
  }

  private stopVideo(): void {
    if (this.mediaRecorder.state === 'inactive') {
      return;
    }

    this.mediaRecorder.stop();
    this.cd.detectChanges();
  }

  private recordVideo(): void {
    const options = { mimeType: 'video/webm;codecs=vp9' };
    this.mediaRecorder = new MediaRecorder(this.stream, options);

    this.mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        this.blobsRecorded = e.data;
      }
    };

    // event : recording stopped & all blobs sent
    this.mediaRecorder.onstop = () => {
      // create local object URL from the recorded video blobs
      const videoLocal = URL.createObjectURL(this.blobsRecorded);
      this.stream.getTracks()[0].stop();
      this.stream = null;
      this.player.nativeElement.srcObject = null;
      this.player.nativeElement.setAttribute('loop', '');
      this.player.nativeElement.src = videoLocal;
      this.videoUrl = videoLocal;
      this.cd.detectChanges();
    };

    this.mediaRecorder.start();

    new Promise((resolve) => setTimeout(resolve, 5000)).then(() => {
      if (this.mediaRecorder.state === 'inactive') {
        this.stopVideo();
      }
    });

    setTimeout(() => {
      this.mediaRecorder.stop();
      this.cd.detectChanges();
    }, 5000);
  }

  public toggleWebcam(): void {
    this.showWebcam = !this.showWebcam;
  }

  public handleInitError(error: WebcamInitError): void {
    this.errors.push(error);
  }

  public handleImage(webcamImage: WebcamImage): void {
    this.webcamImage = webcamImage;
  }

  public cameraWasSwitched(deviceId: string): void {
    this.deviceId = deviceId;
  }

  public get triggerObservable(): Observable<void> {
    return this.trigger.asObservable();
  }

  public get nextWebcamObservable(): Observable<boolean | string> {
    return this.nextWebcam.asObservable();
  }

  closeModal(): void {
    this.dialogRef.close();
  }
}
